<template>
	<ViewWrapper :carregando="loading" :permissao="PERMISSOES_FGTAS.GERENCIAR_ACESSOS">
		<template #titulo>
			Gerenciar Acessos
		</template>
		<template #botaoHeader>
			<v-row justify="center">
				<v-col cols="6" class="align-content-center">
					<v-btn
						block
						:color="corTexto"
						variant="outlined"
						text="LIMPAR"
						@click="limparFiltros"
					></v-btn>
				</v-col>
				<v-col cols="6" class="align-content-center">
					<v-btn
						block
						color="primary"
						text="PESQUISAR"
						@click="consultaUsuarios()"
					></v-btn>
				</v-col>
			</v-row>
		</template>
		<v-container fluid>
			<v-row>
				<v-col cols="12" md="4">
					<div class="mb-2 mr-6 text-hidden">Nome</div>
					<v-text-field v-model="filtros.nome" />
				</v-col>
				<v-col cols="12" md="4">
					<div class="mb-2 mr-6 text-hidden">CPF</div>
					<v-mask-input v-model="filtros.cpf" mask="###.###.###-##" />
				</v-col>
				<v-col cols="12" md="4" align="center">
					<v-checkbox v-model="filtros.usuarioInterno" :color="corTexto" class="mt-6" label="Usuário Interno" />
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12">
					<v-data-table
						:loading="loading"
						:page="pagination.currentPage"
						:items-per-page="pagination.perPage"
						:headers="headers"
						:server-items-length="pagination.totalRows"
						:items="listaUsuarios"
						no-data-text="Nenhum dado encontrado!"
						loading-text="Carregando..."
						:footer-props="{
							itemsPerPageText: 'Itens por página: ',
							disableItemsPerPage:true,
							disablePagination:true,
						}"
						hide-default-footer
						>
						<template v-slot:item.usuarioInterno="{ item }">
							<div align="center">
								<v-icon :color="item.usuarioInterno ? 'success' : 'error'" :icon="item.usuarioInterno ? 'mdi-check' : 'mdi-close'" size="large" />
							</div>
						</template>
						<template v-slot:item.acoes="{ item }">
							<v-menu offset-y>
								<template v-slot:activator="{ props }">
									<v-btn
										variant="outlined"
										color="gray600"
										border="none"
										icon="mdi-dots-horizontal"
										v-bind="props"
										/>
								</template>
								<v-list dense>
									<v-list-item
										:to="`/admin/gerenciar-acessos/${item.id}`"
										link
										density="compact"
										prepend-icon="mdi-eye-outline"
										color="black"
										title="Visualizar"
										/>
								</v-list>
							</v-menu>
						</template>
					</v-data-table>
					<v-container class="mt-0 pa-0">
						<v-row class="pa-0">
							<v-col class="ml-2 d-flex align-center">
								<span class="mr-5 mt-3 fs-15">{{ textoPaginacao }}</span>
							</v-col>
							<v-col class="d-flex justify-end">
								<v-pagination
									:disabled="loading"
									total-visible=7
									:model-value="pagination.currentPage"
									@update:model-value="(event) => pagination.currentPage = event"
									:length="pagination.totalPages"
									/>
							</v-col>
						</v-row>
					</v-container>
				</v-col>
			</v-row>
		</v-container>
	</ViewWrapper>
</template>
<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import ViewWrapper from '@/components/commons/wrapper/ViewWrapper.vue';
import { listarAdmin } from '@/services/UsuarioService';
import { ErroAlert } from "@/utils/AlertUtils.js";
import { vuetify } from '@/plugins/vuetify'
import { formatarCpf } from "@/utils/CpfUtils.js";
import { PERMISSOES_FGTAS } from '@/utils/PermissaoUtils';

const headers = [
	{ key: "cpf", title: "CPF", sortable: false, value: (val) => formatarCpf(val.cpf) },
	{ key: "nome", title: "NOME", sortable: false },
	{ key: "email", title: "E-MAIL", sortable: false },
	{ key: "usuarioInterno", title: "Usuário Interno", align: "center", sortable: false },
	{ key: "acoes", title: "AÇÕES", sortable: false, align: "center" },
];

const loading = ref(false);
const listaUsuarios = ref([]);
const filtros = ref({
	nome: undefined,
	cpf: undefined,
	usuarioInterno: true,
});
const pagination = ref({
	page: 0,
	size: 10,
	perPage: 10,
	currentPage: 1,
	totalRows: 0,
	totalPages: 0,
});

const corTexto = computed(() => {
	return vuetify.theme.current.value.dark ? "white" : "primary";
});

const textoPaginacao = computed(() => {
	if(pagination.value.totalRows){
		if(pagination.value.totalRows === 0){
			return "Nenhum registro localizado";
		}
		if(pagination.value.totalRows === 1){
			return "1 registro recuperado";
		}
		return `${pagination.value.totalRows} registros recuperados`;
	}
	return "";
});

async function consultaUsuarios(page) {
	pagination.value.currentPage = page ? pagination.value.currentPage : 1;
	pagination.value.page = page ? pagination.value.currentPage - 1 : 0;
	loading.value = true;
	await listarAdmin({
		page: pagination.value.page,
		size: pagination.value.size,
		perPage: pagination.value.perPage,
		currentPage: pagination.value.currentPage,
	}, {
		nome: filtros.value.nome ? filtros.value.nome : undefined,
		cpf: filtros.value.cpf ? filtros.value.cpf : undefined,
		usuarioInterno: filtros.value.usuarioInterno,
	})
		.then(({ data }) => {
			pagination.value.size = data.size;
			pagination.value.totalPages = data.totalPages;
			listaUsuarios.value = data.content;
			pagination.value.totalRows = data.totalElements;
		})
		.catch((error) => {
			ErroAlert({ mensagem: "Não foi possível lista os usuários no momento. Tente novamente mais tarde!", error });
		})
		.finally(() => loading.value = false);
}

async function limparFiltros() {
	filtros.value.nome = undefined;
	filtros.value.cpf = undefined;
	filtros.value.usuarioInterno = true;
	await consultaUsuarios();
}

watch(() => pagination.value.currentPage, async (newValue, oldValue) => {
	if (newValue !== oldValue) {
		await consultaUsuarios(newValue);
	}
}, { deep: true });

onMounted(async () => {
	await consultaUsuarios();
});
</script>
